/**
 * MbuziPlusExam.jsx
 * Final exam for the MbuziPlus Course by Imfuyo Academy.
 * 100 multiple-choice questions drawn per attempt from the course question bank
 * (every module quiz across all eight parts), pass mark 70%. Answers are kept in
 * the browser until submitted. The result is saved to the student's course record
 * and a PDF certificate with their name can be downloaded once they pass.
 */
import { useState, useMemo } from "react";
import { requestCertificate } from "../../lib/cover";
import { ChevronLeft, ArrowRight, ArrowLeft, Award, Download, RotateCcw, Check, X, Loader2 } from "lucide-react";
import { db } from "../../Libs/firebase-config.mjs";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { makeCertificate } from "./certificate";
import { drawExam, EXAM_SIZE, BANK_SIZE } from "./examBank";

const GREEN = "#3E7A32";
const DEEP = "#285020";
export const PASS_MARK = 70;


const TOTAL = EXAM_SIZE;
const SAVE_KEY = (token) => `imfuyo_exam_v2_${token || "anon"}`;

export default function MbuziPlusExam({ isDark, user, profile, topics, onBack, onSaved }) {
  // An attempt is a seed plus answers; it is kept in the browser so a lost connection or a closed tab does not lose work.
  const [attempt, setAttempt] = useState(() => {
    try { const raw = localStorage.getItem(SAVE_KEY(user?.token)); if (raw) { const v = JSON.parse(raw); if (v && v.seed) return v; } } catch (e) { /* ignore */ }
    return null;
  });
  const seed = attempt ? attempt.seed : 0;
  const EXAM = useMemo(() => (seed ? drawExam(seed) : []), [seed]);
  const [stage, setStage] = useState(attempt && !attempt.done ? "exam" : "intro"); // intro | exam | result
  const [i, setI] = useState(attempt ? Math.min(attempt.i || 0, TOTAL - 1) : 0);
  const [answersState, setAnswersState] = useState(attempt ? attempt.answers || {} : {});
  const answers = answersState;
  const persist = (next) => { try { localStorage.setItem(SAVE_KEY(user?.token), JSON.stringify(next)); } catch (e) { /* ignore */ } };
  const setAnswers = (a) => { setAnswersState(a); if (attempt) persist({ ...attempt, answers: a, i }); };
  const startAttempt = () => {
    const seed = (Date.now() ^ Math.floor(Math.random() * 1e9)) >>> 0;
    const next = { seed, answers: {}, i: 0, done: false };
    setAttempt(next); persist(next); setAnswersState({}); setI(0); setStage("exam");
  };
  const [name, setName] = useState((profile?.fullName || user?.fullName || "").trim());
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  const bg = isDark ? "#0d1a0f" : "#f5f9f5";
  const card = isDark ? "#14231a" : "#ffffff";
  const ink = isDark ? "#f0f7f0" : "#0d1a0f";
  const sub = isDark ? "#9fb7a2" : "#4b6a50";
  const line = isDark ? "#1e3321" : "#d8edd8";
  const soft = isDark ? "#16301c" : "#eef6ea";

  const score = EXAM.reduce((n, q, k) => n + (answers[k] === q.answer ? 1 : 0), 0);
  const percent = Math.round((score / TOTAL) * 100);
  const passed = percent >= PASS_MARK;
  const progress = stage === "result" ? 100 : Math.round((Object.keys(answers).length / TOTAL) * 100);

  const btn = (primary, disabled) => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 22px", borderRadius: 999,
    fontWeight: 700, fontSize: 14.5, fontFamily: "inherit", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
    border: primary ? "none" : `1.5px solid ${line}`, background: primary ? DEEP : "transparent", color: primary ? "#fff" : ink,
  });

  const finish = async () => {
    setStage("result");
    try { localStorage.removeItem(SAVE_KEY(user?.token)); } catch (e) { /* ignore */ }
    if (attempt) setAttempt({ ...attempt, done: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      await setDoc(
        doc(db, "imfuyo_academy", user.token, "mbuziplus_course", user.pid || user.token),
        { exam: { score, total: TOTAL, percent, passed, bankSize: BANK_SIZE, attemptedAt: serverTimestamp() }, updatedAt: serverTimestamp() },
        { merge: true }
      );
      onSaved && onSaved({ score, total: TOTAL, percent, passed });
    } catch (e) {
      setSaveErr("Your result could not be saved to your account, but you can still download your certificate now.");
    }
  };

  const download = async () => {
    if (!name.trim()) return;
    setBusy(true);
    const d = new Date();
    const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
    const certId = `IMF-MBZ-${stamp}-${String(user.token || "").replace(/[^A-Z0-9]/gi, "").slice(-4).toUpperCase()}`;
    if (passed) requestCertificate({ name: name.trim(), accessCode: user.token, phone: user.phone, score, outOf: TOTAL });
    try {
      await makeCertificate({ name: name.trim(), date, certId, topics });
    } finally {
      setBusy(false);
    }
  };

  const Bar = () => (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, fontWeight: 700, color: sub, marginBottom: 6 }}>
        <span>{stage === "result" ? "Exam complete" : `Question ${i + 1} of ${TOTAL}`}</span>
        <span>{progress}% answered</span>
      </div>
      <div role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} style={{ height: 8, borderRadius: 99, background: soft, overflow: "hidden" }}>
        <div style={{ width: `${progress}%`, height: "100%", background: `linear-gradient(90deg, ${GREEN}, ${DEEP})`, transition: "width .3s ease" }} />
      </div>
    </div>
  );

  let body;
  if (stage === "intro") {
    body = (
      <div>
        <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: GREEN, margin: "0 0 6px" }}>Final exam</p>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(28px,4vw,38px)", margin: "0 0 12px", color: ink, lineHeight: 1.12 }}>MbuziPlus Course exam</h1>
        <p style={{ fontSize: 16.5, lineHeight: 1.7, color: sub, margin: "0 0 18px", maxWidth: 600 }}>
          Welcome, MbuziPlus Student. This examination has {TOTAL} multiple-choice questions drawn from a bank of {BANK_SIZE}, covering all eight parts of the course. Every attempt is a different set. You need {PASS_MARK}% to pass and earn your Certificate of Completion with your name.
        </p>
        <ul style={{ margin: "0 0 22px", paddingLeft: 20, color: sub, lineHeight: 1.9, fontSize: 15.5 }}>
          <li>Answers are marked at the end, with the reason for each.</li>
          <li>You can go back and change an answer before you submit.</li>
          <li>If you do not pass, you can take the exam again with a new set of questions.</li>
          <li>Your answers are kept on this phone or computer until you submit, so you can stop and continue later.</li>
        </ul>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button style={btn(true)} onClick={startAttempt}>Start the exam <ArrowRight size={16} /></button>
          <button style={btn(false)} onClick={onBack}>Back to the course</button>
        </div>
      </div>
    );
  } else if (stage === "exam" && EXAM.length) {
    const q = EXAM[i];
    const last = i === TOTAL - 1;
    const allDone = Object.keys(answers).length === TOTAL;
    body = (
      <div>
        <Bar />
        <h2 style={{ fontSize: "clamp(19px,2.6vw,24px)", margin: "0 0 18px", color: ink, lineHeight: 1.35 }}>{q.q}</h2>
        <div style={{ display: "grid", gap: 10 }} role="radiogroup">
          {q.options.map((o, k) => {
            const on = answers[i] === k;
            return (
              <button key={o} role="radio" aria-checked={on} onClick={() => setAnswers({ ...answers, [i]: k })}
                style={{ textAlign: "left", padding: "14px 16px", borderRadius: 14, border: `2px solid ${on ? GREEN : line}`, background: on ? soft : card, color: ink, fontSize: 15.5, lineHeight: 1.5, cursor: "pointer", fontFamily: "inherit" }}>
                {o}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 22 }}>
          <button style={btn(false, i === 0)} disabled={i === 0} onClick={() => { setI(i - 1); if (attempt) persist({ ...attempt, answers, i: i - 1 }); }}><ArrowLeft size={16} /> Back</button>
          {!last && <button style={btn(true, answers[i] === undefined)} disabled={answers[i] === undefined} onClick={() => { setI(i + 1); if (attempt) persist({ ...attempt, answers, i: i + 1 }); }}>Next <ArrowRight size={16} /></button>}
          {last && <button style={btn(true, !allDone)} disabled={!allDone} onClick={finish}>Submit exam <Check size={16} /></button>}
        </div>
        {last && !allDone && <p style={{ color: sub, fontSize: 13.5, marginTop: 10 }}>Answer every question to submit. Use Back to find any you skipped.</p>}
      </div>
    );
  } else {
    body = (
      <div>
        <Bar />
        <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: passed ? GREEN : "#B42318", margin: "0 0 6px" }}>{passed ? "Passed" : "Not passed yet"}</p>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(28px,4vw,38px)", margin: "0 0 10px", color: ink }}>{score} out of {TOTAL} ({percent}%)</h1>
        <p style={{ fontSize: 16.5, lineHeight: 1.7, color: sub, margin: "0 0 20px", maxWidth: 600 }}>
          {passed ? "Congratulations, MbuziPlus Student. You passed the exam. Check your name below and download your certificate." : `You need ${PASS_MARK}% to pass. Read the reasons below, revisit the lessons, and take the exam again when you are ready.`}
        </p>
        {saveErr && <p style={{ color: "#B42318", fontSize: 13.5 }}>{saveErr}</p>}
        {passed && (
          <div style={{ padding: 20, borderRadius: 18, background: soft, border: `1.5px solid ${line}`, marginBottom: 24 }}>
            <label htmlFor="cert-name" style={{ display: "block", fontSize: 13, fontWeight: 700, color: sub, marginBottom: 6 }}>Name to print on your certificate</label>
            <input id="cert-name" value={name} onChange={(e) => setName(e.target.value)} style={{ width: "100%", maxWidth: 420, padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${line}`, background: card, color: ink, fontSize: 16, fontFamily: "inherit", boxSizing: "border-box" }} />
            <div style={{ marginTop: 14 }}>
              <button style={btn(true, busy || !name.trim())} disabled={busy || !name.trim()} onClick={download}>
                {busy ? <Loader2 size={16} style={{ animation: "mbspin 0.75s linear infinite" }} /> : <Download size={16} />} Download my certificate (PDF)
              </button>
            </div>
          </div>
        )}
        <h3 style={{ margin: "0 0 12px", fontSize: 18, color: ink }}>Review</h3>
        <div style={{ display: "grid", gap: 12 }}>
          {EXAM.map((q, k) => {
            const ok = answers[k] === q.answer;
            return (
              <div key={q.q} style={{ padding: "14px 16px", borderRadius: 14, border: `1.5px solid ${ok ? line : "#f1b7b0"}`, background: card }}>
                <p style={{ margin: "0 0 6px", fontWeight: 700, color: ink, fontSize: 15 }}>{k + 1}. {q.q}</p>
                <p style={{ margin: "0 0 4px", fontSize: 14, color: ok ? GREEN : "#B42318", display: "flex", gap: 6, alignItems: "center" }}>
                  {ok ? <Check size={15} /> : <X size={15} />} Your answer: {q.options[answers[k]]}
                </p>
                {!ok && <p style={{ margin: "0 0 4px", fontSize: 14, color: ink }}>Correct answer: {q.options[q.answer]}</p>}
                <p style={{ margin: 0, fontSize: 14, color: sub, lineHeight: 1.6 }}>{q.why}</p>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 22 }}>
          <button style={btn(false)} onClick={() => { setStage("intro"); setAnswersState({}); setI(0); setAttempt(null); }}><RotateCcw size={15} /> Take the exam again</button>
          <button style={btn(true)} onClick={onBack}>Back to the course</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif", paddingTop: 80 }}>
      <style>{"@keyframes mbspin{to{transform:rotate(360deg)}}"}</style>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "20px clamp(16px,4vw,32px) 80px" }}>
        <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${line}`, borderRadius: 50, padding: "7px 16px", fontSize: 12, fontWeight: 600, color: sub, cursor: "pointer", fontFamily: "inherit", marginBottom: 18 }}>
          <ChevronLeft size={13} /> Back to the course
        </button>
        <div style={{ background: card, border: `1px solid ${line}`, borderRadius: 24, padding: "clamp(20px,4vw,36px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Award size={16} color={GREEN} />
            <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: GREEN }}>MbuziPlus Course · Imfuyo Academy</span>
          </div>
          {body}
        </div>
      </div>
    </div>
  );
}
